import React, { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from '../../store';
import { answerQuestion, setCurrentPage, setViewed } from '../../store/trail';
import { trailEvent } from '../../utils/trailProgress';
// A static import here is fine, and NOT what keeps @techstark/opencv-js
// (~10 MB) out of the main chunk: compareImages.ts never statically imports
// opencv-js itself — it lazily dynamic-imports it inside getCv() (see that
// file), which is what gives Rollup/rolldown the actual code-split boundary.
// Trail.tsx already statically imports this same module (for
// preloadOpenCV()), so a dynamic import here would be a no-op re-fetch of an
// already-loaded module — confirmed by rolldown's own
// INEFFECTIVE_DYNAMIC_IMPORT warning when this was tried.
import { compareImages } from '../../utils/compareImages';

import Loading from '../Loading';
import { ErrorBoundary, PageRenderer } from 'magical-trails-react';
import {
  isHydratedLocationStoryPoint,
  isUnlocked,
  messageDeliveryFor,
} from 'magical-trails';

export interface PageParams {
  pointId: string;
  type: string;
}

export interface Props {
  selectedPoint?: string;
  setSelectedPoint: (code: string) => void;
  handleClose: () => void;
}

const Pages: React.FC<Props> = ({
  selectedPoint,
  setSelectedPoint,
  handleClose,
}: Props) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  let { pointId } = useParams<PageParams>();
  const storyPoint = useSelector((state: RootState) =>
    state.trail.currentStory?.storyPoints.find(({ code }) => code === pointId)
  );
  // Only a trail that has finished loading can be missing a point for real:
  // mid-load, every point is "missing".
  const storyLoaded = useSelector(
    (state: RootState) =>
      state.trail.trailLoaded &&
      !state.trail.loading &&
      Boolean(state.trail.currentStory)
  );

  useEffect(() => {
    if (pointId !== selectedPoint) {
      setSelectedPoint(pointId);
    }
  }, [pointId, selectedPoint, setSelectedPoint]);

  // A live trail edit can delete the very stop the player has open (see
  // utils/liveTrailUpdates.ts). Nothing will ever render for it, so send them
  // back to the map instead of leaving them on a spinner forever. Guarded by
  // a ref because ModalRouter hands us a fresh `handleClose` every render:
  // without it, closing would be requested again on each of those renders.
  const closedForMissingPointRef = useRef(false);
  useEffect(() => {
    closedForMissingPointRef.current = false;
  }, [pointId]);
  useEffect(() => {
    if (!storyLoaded || storyPoint || closedForMissingPointRef.current) return;
    closedForMissingPointRef.current = true;
    handleClose();
  }, [storyLoaded, storyPoint, handleClose]);

  // Phone shell: a message only opens here as a delivered dossier
  // (`presentation: 'attachment'`). A chat-bubble message, or one still
  // locked or inside its delay, reached by a typed URL would otherwise be
  // read full screen AND in its thread, and marking it viewed here would let
  // the status pass finish it before its conversation had.
  const storyConfig = useSelector(
    (state: RootState) => state.trail.currentStory?.config
  );
  const delivery = storyPoint
    ? messageDeliveryFor(storyPoint, storyConfig)
    : undefined;
  const notForFullScreen =
    !!storyPoint &&
    !!delivery &&
    (delivery.presentation !== 'attachment' ||
      !isUnlocked(storyPoint) ||
      !storyPoint.deliveredAt ||
      Date.parse(storyPoint.deliveredAt) > Date.now());
  useEffect(() => {
    if (!notForFullScreen || closedForMissingPointRef.current) return;
    closedForMissingPointRef.current = true;
    handleClose();
  }, [notForFullScreen, handleClose]);

  useEffect(() => {
    if (notForFullScreen) return;
    if (!storyPoint?.viewed && storyPoint?.code) {
      dispatch(setViewed({ code: storyPoint.code }));
    }
  }, [storyPoint, notForFullScreen, dispatch]);

  // PageRenderer is memoized on handler identity: keep these stable so
  // unrelated store updates don't remount heavy pages (SnapChase etc.).
  const currentPage = storyPoint?.currentPage || 0;
  const pageCount = storyPoint?.pages?.length || 1;

  // The raw slice action creator only *builds* the action; it must be
  // dispatched or answers never reach the store (and never persist).
  const handleAnswerQuestion = useCallback(
    (payload: { code: string; page: number; userAnswer: string }) => {
      dispatch(answerQuestion(payload));
    },
    [dispatch]
  );

  const handlenextPage = useCallback(() => {
    if (currentPage < pageCount - 1) {
      dispatch(
        setCurrentPage({
          code: pointId,
          currentPage: currentPage + 1,
        })
      );
    }
  }, [currentPage, pageCount, pointId, dispatch]);

  const handleprevPage = useCallback(() => {
    if (currentPage > 0) {
      dispatch(
        setCurrentPage({
          code: pointId,
          currentPage: currentPage - 1,
        })
      );
    }
  }, [currentPage, pointId, dispatch]);

  const handleSetPage = useCallback(
    (page: number) => {
      dispatch(
        setCurrentPage({
          code: pointId,
          currentPage: page,
        })
      );
    },
    [pointId, dispatch]
  );

  const handleTrailEvent = useCallback(
    (event: string, properties?: Record<string, string | number | boolean>) => {
      // Read state at call time so the callback stays identity-stable
      // without capturing a stale snapshot.
      trailEvent(event, store.getState(), pointId, properties);
    },
    [store, pointId]
  );

  return !notForFullScreen &&
    typeof storyPoint?.currentPage === 'number' &&
    storyPoint?.pages?.[storyPoint?.currentPage] ? (
    // A page that throws outside its own guard (the shell around a game, a
    // narrative page, PageRenderer itself) used to escalate to the app-level
    // boundary — a "Reload" card with the whole trail gone. Contain it here,
    // keyed per page so moving on resets the boundary, with a way onward.
    <ErrorBoundary
      key={`${pointId}:${storyPoint.currentPage}`}
      fallback={
        <PageCrashFallback
          onSkip={
            (storyPoint.pages?.length ?? 0) - 1 > storyPoint.currentPage
              ? handlenextPage
              : undefined
          }
          onClose={handleClose}
        />
      }
    >
      <PageRenderer
        pages={storyPoint.pages}
        code={pointId}
        currentPage={storyPoint.currentPage}
        answerQuestion={handleAnswerQuestion}
        handlenextPage={handlenextPage}
        handleprevPage={handleprevPage}
        handleClose={handleClose}
        handleSetPage={handleSetPage}
        trailEvent={handleTrailEvent}
        compareImages={compareImages}
        latlng={
          isHydratedLocationStoryPoint(storyPoint)
            ? storyPoint.latlng
            : undefined
        }
      />
    </ErrorBoundary>
  ) : (
    <Loading />
  );
};

/** Shown when a page crashes: keep the trail alive and offer a way onward. */
const PageCrashFallback: React.FC<{
  onSkip?: () => void;
  onClose: () => void;
}> = ({ onSkip, onClose }) => (
  <div
    role="alert"
    className="flex h-full w-full flex-col items-center justify-center bg-black p-6 text-center"
  >
    <div className="mb-2 text-3xl" aria-hidden="true">
      🧭
    </div>
    <p className="text-sm font-semibold text-white">This page hit a snag</p>
    <p className="mt-1 max-w-xs text-xs text-white/70">
      The rest of the trail is fine — carry on and come back to this one later.
    </p>
    <div className="mt-4 flex gap-2">
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-xs"
        >
          Next page
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center justify-center rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 hover:bg-white/20"
      >
        Back to the map
      </button>
    </div>
  </div>
);

export default Pages;
